import { Icon, TooltipOverflowMode } from "@fluentui/react";
import React from "react";
import ClientCommentJsonDisplay from "../../../components/ClientCommentJsonDisplay";
import FluentTooltip from "../../../components/FluentTooltip";
import { Truncate } from "../../../components/styled";
import { accent } from "../../../config/colors";
import { formatNumber } from "../../../utils/number";
import {
  formatDateString,
  formatDateStringWithTime,
} from "../../../utils/utils";
import CodeCell from "../../fleets/FleetList/columnCells/CodeCell";
import {
  BookingAmountCell,
  TotalBookingAmountCell,
} from "./columnCells/BookingAmountCell";
import {
  getBookingAmount,
  getTotalBookingAmount,
} from "../../../utils/bookingAmount";
import StatusCell from "../StatusCell/StatusCell";
import CheckColumn from "./columnCells/CheckColumn";
import ColumnHoverCard from "./columnCells/ColumnHoverCard";
import ExcludeHorsePowerBasedTaxReason from "./columnCells/ExcludeHorsePowerBasedTaxReason";
import InsuranceLineIconsCell from "./columnCells/InsuranceLineIconsCell";
import LicensePlateCell from "./columnCells/LicensePlateCell/LicensePlateCell";
import LineInsurer from "./columnCells/LineInsurer";
import VehicleVin from "./columnCells/VehicleVin";

export const defaultColumns: any[] = [
  {
    key: "vehicleStatusCode",
    isResizable: true,
    fieldName: "vehicleStatusCode",
    labelKey: "bfm.vehicleStatus.label",
    name: "bfm.vehicleStatus.label",
    maxWidth: 100,
    minWidth: 100,
    isVisible: true,
    isSortable: true,
    isSorted: false,
    isSortedDescending: true,

    isFixed: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleStatusCode",
      value: [],
      exclude: [
        "VehicleStatusCode.ALL",
        "VehicleStatusCode.NOT_DEACTIVATED_OR_REFUSED",
      ],
      renderLabel: ({ taxonomy, t }) => {
        const currentTheme = localStorage.getItem("darkMode");
        let color;

        if (currentTheme === "true") {
          color = "white";
        } else {
          color = "black";
        }
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      width: 150,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleStatusCode.byId[row.vehicleStatusCode]?.code),
      isSortable: false,
    },

    onRender: (row) => {
      if (!row) return;
      return <StatusCell vehicle={row} />;
    },
  },
  {
    key: "licensePlate",
    isResizable: true,
    minWidth: 220,
    maxWidth: 220,
    isRowHeader: true,
    fieldName: "licensePlate",
    labelKey: "bfm.vehicleLicensePlate.label",
    name: "bfm.vehicleLicensePlate.label",
    filter: {
      type: "text",
      value: "",
    },
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isFixed: true,
    onRender: (row) => {
      if (!row) return null;
      return <LicensePlateCell vehicle={row} />;
    },
    isSorted: false,
    isSortedDescending: true,
    sortAscendingAriaLabel: "Sorted A to Z",
    sortDescendingAriaLabel: "Sorted Z to A",
  },
  {
    key: "fleetName",
    isResizable: true,
    fieldName: "fleetName",
    labelKey: "bfm.fleetName",
    name: "bfm.fleetName",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isFixed: true,
  },
  {
    key: "options",
    isResizable: true,
    fieldName: "options",
    labelKey: "greco.options",
    name: "",
    minWidth: 20,
    maxWidth: 20,
    isVisible: true,
    isFixed: true,
    isPresentInColumnOptions: false,
    isSortable: false,
    excel: {
      isVisible: false,
      isSortable: false,
    },
    // onRender: (row) => {
    //   if (!row) return null;
    //   return (
    //     <span className="action-icon">
    //       <ContextButton vehicle={row} />;
    //     </span>
    //   );
    // },
  },
  {
    key: "insuranceLineIcons",
    isResizable: true,
    fieldName: "insuranceLineIcons",
    labelKey: "",
    name: "",
    minWidth: 400,
    maxWidth: 400,
    isVisible: true,
    isFixed: false,
    isPresentInColumnOptions: false,
    isSortable: false,
    excel: {
      isVisible: false,
      isResizable: true,
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return <InsuranceLineIconsCell vehicle={row} />;
    },
  },
  {
    key: "mtplPolicyStatus",
    isResizable: true,
    iconName: "Sort",
    fieldName: "mtplPolicyStatus",
    labelKey: "bfm.vehicleForm.mtplPolicyStatus.label",
    name: "bfm.vehicleForm.mtplPolicyStatus.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePolicyStatusCode",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(taxonomy.VehiclePolicyStatusCode.byId[row?.mtplPolicyStatus]?.code),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehiclePolicyStatusCode"}
          propertyCode={row?.mtplPolicyStatus}
        />
      );
    },
  },
  {
    key: "mtplPolicyStatusComment",
    isResizable: true,
    fieldName: "mtplPolicyStatusComment",
    labelKey: "bfm.vehicleForm.mtplPolicyStatusComment.label",
    name: "bfm.vehicleForm.mtplPolicyStatusComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.mtplPolicyStatusComment}
      >
        {row?.mtplPolicyStatusComment}
      </FluentTooltip>
    ),
  },
  {
    key: "mtplPolicyStatusDateTime",
    isResizable: true,
    fieldName: "mtplPolicyStatusDateTime",
    labelKey: "bfm.vehicleForm.mtplPolicyStatusDateTime.label",
    name: "bfm.vehicleForm.mtplPolicyStatusDateTime.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "datetime",
      render: ({ row }) =>
        row?.mtplPolicyStatusDateTime
          ? new Date(row?.mtplPolicyStatusDateTime)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplPolicyStatusDateTime
            ? formatDateStringWithTime(row?.mtplPolicyStatusDateTime)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullPolicyStatus",
    isResizable: true,
    iconName: "Sort",
    fieldName: "hullPolicyStatus",
    labelKey: "bfm.vehicleForm.hullPolicyStatus.label",
    name: "bfm.vehicleForm.hullPolicyStatus.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePolicyStatusCode",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(taxonomy.VehiclePolicyStatusCode.byId[row?.hullPolicyStatus]?.code),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehiclePolicyStatusCode"}
          propertyCode={row?.hullPolicyStatus}
        />
      );
    },
  },
  {
    key: "hullPolicyStatusComment",
    isResizable: true,
    fieldName: "hullPolicyStatusComment",
    labelKey: "bfm.vehicleForm.hullPolicyStatusComment.label",
    name: "bfm.vehicleForm.hullPolicyStatusComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.hullPolicyStatusComment}
      >
        {row?.hullPolicyStatusComment}
      </FluentTooltip>
    ),
  },
  {
    key: "hullPolicyStatusDateTime",
    isResizable: true,
    fieldName: "hullPolicyStatusDateTime",
    labelKey: "bfm.vehicleForm.hullPolicyStatusDateTime.label",
    name: "bfm.vehicleForm.hullPolicyStatusDateTime.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "datetime",
      render: ({ row }) =>
        row?.hullPolicyStatusDateTime
          ? new Date(row?.hullPolicyStatusDateTime)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullPolicyStatusDateTime
            ? formatDateStringWithTime(row?.hullPolicyStatusDateTime)
            : null}
        </span>
      );
    },
  },
  {
    key: "fleetLegalPolicyStatus",
    isResizable: true,
    iconName: "Sort",
    fieldName: "fleetLegalPolicyStatus",
    labelKey: "bfm.vehicleForm.fleetLegalPolicyStatus.label",
    name: "bfm.vehicleForm.fleetLegalPolicyStatus.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePolicyStatusCode",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.VehiclePolicyStatusCode.byId[row?.fleetLegalPolicyStatus]
            ?.code,
        ),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehiclePolicyStatusCode"}
          propertyCode={row?.fleetLegalPolicyStatus}
        />
      );
    },
  },
  {
    key: "fleetLegalPolicyStatusDateTime",
    isResizable: true,
    fieldName: "fleetLegalPolicyStatusDateTime",
    labelKey: "bfm.vehicleForm.fleetLegalPolicyStatusDateTime.label",
    name: "bfm.vehicleForm.fleetLegalPolicyStatusDateTime.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "datetime",
      render: ({ row }) =>
        row?.fleetLegalPolicyStatusDateTime
          ? new Date(row?.fleetLegalPolicyStatusDateTime)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.fleetLegalPolicyStatusDateTime
            ? formatDateStringWithTime(row?.fleetLegalPolicyStatusDateTime)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullOnLandPolicyStatus",
    isResizable: true,
    iconName: "Sort",
    fieldName: "hullOnLandPolicyStatus",
    labelKey: "bfm.vehicleForm.hullOnLandPolicyStatus.label",
    name: "bfm.vehicleForm.hullOnLandPolicyStatus.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePolicyStatusCode",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.VehiclePolicyStatusCode.byId[row?.hullOnLandPolicyStatus]
            ?.code,
        ),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehiclePolicyStatusCode"}
          propertyCode={row?.hullOnLandPolicyStatus}
        />
      );
    },
  },
  {
    key: "hullOnLandPolicyStatusDateTime",
    isResizable: true,
    fieldName: "hullOnLandPolicyStatusDateTime",
    labelKey: "bfm.vehicleForm.hullOnLandPolicyStatusDateTime.label",
    name: "bfm.vehicleForm.hullOnLandPolicyStatusDateTime.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "datetime",
      render: ({ row }) =>
        row?.hullOnLandPolicyStatusDateTime
          ? new Date(row?.hullOnLandPolicyStatusDateTime)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullOnLandPolicyStatusDateTime
            ? formatDateStringWithTime(row?.hullOnLandPolicyStatusDateTime)
            : null}
        </span>
      );
    },
  },

  {
    key: "machineryBreakdownPolicyStatus",
    isResizable: true,
    iconName: "Sort",
    fieldName: "machineryBreakdownPolicyStatus",
    labelKey: "bfm.vehicleForm.machineryBreakdownPolicyStatus.label",
    name: "bfm.vehicleForm.machineryBreakdownPolicyStatus.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehiclePolicyStatusCode",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.VehiclePolicyStatusCode.byId[
            row?.machineryBreakdownPolicyStatus
          ]?.code,
        ),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehiclePolicyStatusCode"}
          propertyCode={row?.machineryBreakdownPolicyStatus}
        />
      );
    },
  },
  {
    key: "machineryBreakdownPolicyStatusDateTime",
    isResizable: true,
    fieldName: "machineryBreakdownPolicyStatusDateTime",
    labelKey: "bfm.vehicleForm.machineryBreakdownPolicyStatusDateTime.label",
    name: "bfm.vehicleForm.machineryBreakdownPolicyStatusDateTime.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "datetime",
      render: ({ row }) =>
        row?.machineryBreakdownPolicyStatusDateTime
          ? new Date(row?.machineryBreakdownPolicyStatusDateTime)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.machineryBreakdownPolicyStatusDateTime
            ? formatDateStringWithTime(
                row?.machineryBreakdownPolicyStatusDateTime,
              )
            : null}
        </span>
      );
    },
  },

  {
    key: "vehicleIssuingStatusCode",
    isResizable: true,
    iconName: "Sort",
    fieldName: "vehicleIssuingStatusCode",
    labelKey: "bfm.vehicleForm.vehicleIssuingStatusCode.label",
    name: "bfm.vehicleForm.vehicleIssuingStatusCode.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    isSortable: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleIssuingStatus",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.VehicleIssuingStatus.byId[row?.vehicleIssuingStatusCode]
            ?.code,
        ),
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehicleIssuingStatus"}
          propertyCode={row?.vehicleIssuingStatusCode}
        />
      );
    },
  },
  {
    key: "totalAnnualGrossPremium",
    isResizable: true,
    fieldName: "totalAnnualGrossPremium",
    labelKey: "bfm.totalAnnualGrossPremium",
    name: "bfm.totalAnnualGrossPremium",
    minWidth: 150,
    maxWidth: 200,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.totalAnnualGrossPremium === null) return null;
        return row.totalAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.totalAnnualGrossPremium === null) return null;
      return formatNumber(row.totalAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountTotal",
    isResizable: true,
    fieldName: "bookingAmountTotal",
    labelKey: "bfm.bookingAmountTotal.label",
    name: "bfm.bookingAmountTotal.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) => getTotalBookingAmount(row, taxonomy),
    },
    onRender: (row) => <TotalBookingAmountCell row={row} />,
  },
  {
    key: "totalMtplRelatedAnnualGrossPremium",
    isResizable: true,
    fieldName: "totalMtplRelatedAnnualGrossPremium",
    labelKey: "bfm.totalMtplRelatedAnnualGrossPremium",
    name: "bfm.totalMtplRelatedAnnualGrossPremium",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.totalMtplRelatedAnnualGrossPremium === null)
          return null;
        return row.totalMtplRelatedAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.totalMtplRelatedAnnualGrossPremium === null) return null;
      return formatNumber(row.totalMtplRelatedAnnualGrossPremium);
    },
  },
  {
    key: "mtplAnnualNetPremium",
    isResizable: true,
    fieldName: "mtplAnnualNetPremium",
    labelKey: "bfm.mtplAnnualNetPremium.label",
    name: "bfm.mtplAnnualNetPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.mtplAnnualNetPremium === null) return null;
        return row.mtplAnnualNetPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.mtplAnnualNetPremium === null) return null;
      return formatNumber(row.mtplAnnualNetPremium);
    },
  },
  {
    key: "hullAnnualGrossPremium",
    isResizable: true,
    fieldName: "hullAnnualGrossPremium",
    labelKey: "bfm.hullAnnualGrossPremium.label",
    name: "bfm.hullAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullAnnualGrossPremium === null) return null;
        return row.hullAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullAnnualGrossPremium === null) return null;
      return formatNumber(row.hullAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountHull",
    isResizable: true,
    fieldName: "bookingAmountHull",
    labelKey: "bfm.bookingAmountHull.label",
    name: "bfm.bookingAmountHull.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.hullAnnualGrossPremium, taxonomy, row?.hullPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.hullAnnualGrossPremium}
        paymentMethodCode={row?.hullPaymentMethod}
      />
    ),
  },
  {
    key: "hullAnnualNetPremium",
    isResizable: true,
    fieldName: "hullAnnualNetPremium",
    labelKey: "bfm.hullAnnualNetPremium.label",
    name: "bfm.hullAnnualNetPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullAnnualNetPremium === null) return null;
        return row.hullAnnualNetPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullAnnualNetPremium === null) return null;
      return formatNumber(row.hullAnnualNetPremium);
    },
  },
  {
    key: "totalProRataGrossPremium",
    isResizable: true,
    fieldName: "totalProRataGrossPremium",
    labelKey: "bfm.totalProRataGrossPremium",
    name: "bfm.totalProRataGrossPremium",
    minWidth: 150,
    maxWidth: 200,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.totalProRataGrossPremium === null) return null;
        return row.totalProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.totalProRataGrossPremium === null) return null;
      return formatNumber(row.totalProRataGrossPremium);
    },
  },
  {
    key: "totalMtplRelatedProRataGrossPremium",
    isResizable: true,
    fieldName: "totalMtplRelatedProRataGrossPremium",
    labelKey: "bfm.totalMtplRelatedProRataGrossPremium",
    name: "bfm.totalMtplRelatedProRataGrossPremium",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.totalMtplRelatedProRataGrossPremium === null)
          return null;
        return row.totalMtplRelatedProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.totalMtplRelatedProRataGrossPremium === null)
        return null;
      return formatNumber(row.totalMtplRelatedProRataGrossPremium);
    },
  },
  {
    key: "hullProRataGrossPremium",
    isResizable: true,
    fieldName: "hullProRataGrossPremium",
    labelKey: "bfm.hullProRataGrossPremium.label",
    name: "bfm.hullProRataGrossPremium.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullProRataGrossPremium === null) return null;
        return row.hullProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullProRataGrossPremium === null) return null;
      return formatNumber(row.hullProRataGrossPremium);
    },
  },
  {
    key: "totalBookingAmount",
    isResizable: true,
    fieldName: "totalBookingAmount",
    labelKey: "bfm.totalPendingBookingAmount",
    name: "bfm.totalPendingBookingAmount",
    minWidth: 150,
    maxWidth: 200,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.totalBookingAmount === null) return null;
        return row.totalBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.totalBookingAmount === null) return null;
      return formatNumber(row.totalBookingAmount);
    },
  },
  {
    key: "mtplRelatedBookingAmount",
    isResizable: true,
    fieldName: "mtplRelatedBookingAmount",
    labelKey: "bfm.mtplRelatedPendingBookingAmount",
    name: "bfm.mtplRelatedPendingBookingAmount",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.mtplRelatedBookingAmount === null) return null;
        return row.mtplRelatedBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.mtplRelatedBookingAmount === null) return null;
      return formatNumber(row.mtplRelatedBookingAmount);
    },
  },
  {
    key: "hullRelatedBookingAmount",
    isResizable: true,
    fieldName: "hullRelatedBookingAmount",
    labelKey: "bfm.hullRelatedPendingBookingAmount",
    name: "bfm.hullRelatedPendingBookingAmount",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullRelatedBookingAmount === null) return null;
        return row.hullRelatedBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullRelatedBookingAmount === null) return null;
      return formatNumber(row.hullRelatedBookingAmount);
    },
  },
  {
    key: "carUsageCode",
    isResizable: true,
    fieldName: "carUsageCode",
    labelKey: "bfm.vehicleCarUsage.label",
    isSortable: true,
    name: "bfm.vehicleCarUsage.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: true,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehicleUsageType",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleUsageType.byId[row?.carUsageCode]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"VehicleUsageType"}
          propertyCode={row?.carUsageCode}
        />
      );
    },
  },
  {
    key: "mtplPaymentMethod",
    isResizable: true,
    fieldName: "mtplPaymentMethod",
    labelKey: "bfm.mtplPaymentMethod.label",
    isSortable: true,
    name: "bfm.mtplPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.mtplPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.mtplPaymentMethod}
        />
      );
    },
  },
  {
    key: "mtplPaymentMean",
    isResizable: true,
    fieldName: "mtplPaymentMean",
    labelKey: "bfm.mtplPaymentMean.label",
    isSortable: true,
    name: "bfm.mtplPaymentMean.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "PremiumPaymentMean",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PremiumPaymentMean.byId[row?.mtplPaymentMean]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PremiumPaymentMean"}
          propertyCode={row?.mtplPaymentMean}
        />
      );
    },
  },
  {
    key: "hullPaymentMethod",
    isResizable: true,
    fieldName: "hullPaymentMethod",
    labelKey: "bfm.hullPaymentMethod.label",
    isSortable: true,
    name: "bfm.hullPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.hullPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.hullPaymentMethod}
        />
      );
    },
  },
  {
    key: "hullPaymentMean",
    isResizable: true,
    fieldName: "hullPaymentMean",
    labelKey: "bfm.hullPaymentMean.label",
    isSortable: true,
    name: "bfm.hullPaymentMean.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "PremiumPaymentMean",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PremiumPaymentMean.byId[row?.hullPaymentMean]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PremiumPaymentMean"}
          propertyCode={row?.hullPaymentMean}
        />
      );
    },
  },
  {
    key: "truckMountingCode",
    isResizable: true,
    fieldName: "truckMountingCode",
    labelKey: "bfm.truckMountingCode.label",
    isSortable: true,
    name: "bfm.truckMountingCode.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "TruckMounting",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.TruckMounting.byId[row?.truckMountingCode]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"TruckMounting"}
          propertyCode={row?.truckMountingCode}
        />
      );
    },
  },
  {
    key: "freeTextField1",
    isResizable: true,
    fieldName: "freeTextField1",
    labelKey: "bfm.freeTextField1.label",
    name: "bfm.freeTextField1.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.freeTextField1}
      >
        {row?.freeTextField1}
      </FluentTooltip>
    ),
  },
  {
    key: "freeTextField2",
    isResizable: true,
    fieldName: "freeTextField2",
    labelKey: "bfm.freeTextField2.label",
    name: "bfm.freeTextField2.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.freeTextField2}
      >
        {row?.freeTextField2}
      </FluentTooltip>
    ),
  },
  {
    key: "vehicleCreatedAt",
    isResizable: true,
    fieldName: "vehicleCreatedAt",
    labelKey: "bfm.vehicleCreatedAt.label",
    name: "bfm.vehicleCreatedAt.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.vehicleCreatedAt ? new Date(row?.vehicleCreatedAt) : null,
    },
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.vehicleCreatedAt
            ? formatDateString(row?.vehicleCreatedAt)
            : null}
        </span>
      );
    },
  },
  {
    key: "vehicleLastModifiedAt",
    isResizable: true,
    fieldName: "vehicleLastModifiedAt",
    labelKey: "bfm.vehicleForm.vehicleLastModifiedAt.label",
    name: "bfm.vehicleForm.vehicleLastModifiedAt.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    iconName: "Sort",
    isSortable: true,
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.vehicleLastModifiedAt
          ? new Date(row?.vehicleLastModifiedAt)
          : null,
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.vehicleLastModifiedAt
            ? formatDateString(row?.vehicleLastModifiedAt)
            : null}
        </span>
      );
    },
  },
  {
    key: "vehicleLastStatusDate",
    isResizable: true,
    fieldName: "vehicleLastStatusDate",
    labelKey: "bfm.vehicleForm.vehicleLastStatusDate.label",
    name: "bfm.vehicleForm.vehicleLastStatusDate.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.vehicleLastStatusDate
          ? new Date(row?.vehicleLastStatusDate)
          : null,
    },
    isSortable: true,
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.vehicleLastStatusDate
            ? formatDateString(row?.vehicleLastStatusDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "vehicleLastStatusComment",
    isResizable: true,
    fieldName: "vehicleLastStatusComment",
    labelKey: "bfm.vehicleForm.vehicleLastStatusComment.label",
    name: "bfm.vehicleForm.vehicleLastStatusComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.vehicleLastStatusComment}
      >
        {row?.vehicleLastStatusComment}
      </FluentTooltip>
    ),
  },

  {
    key: "hasSharedLicensePlate",
    isResizable: true,
    fieldName: "hasSharedLicensePlate",
    labelKey: "bfm.hasSharedLicensePlate.label",
    name: "bfm.hasSharedLicensePlate.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) =>
        row?.hasSharedLicensePlate ? t("greco.yes") : "",
    },
    onRender: (row) =>
      row?.hasSharedLicensePlate && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "hadSharedLicensePlate",
    isResizable: true,
    fieldName: "hadSharedLicensePlate",
    labelKey: "bfm.hadSharedLicensePlate.label",
    name: "bfm.hadSharedLicensePlate.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) =>
        row?.hadSharedLicensePlate ? t("greco.yes") : "",
    },
    onRender: (row) =>
      row?.hadSharedLicensePlate && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },

  {
    key: "hasIndemnityPremium",
    isResizable: true,
    fieldName: "hasIndemnityPremium",
    labelKey: "bfm.hasIndemnityPremium.label",
    name: "bfm.hasIndemnityPremium.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hasIndemnityPremium ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.hasIndemnityPremium && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "hasBonusMalusPremium",
    isResizable: true,
    fieldName: "hasBonusMalusPremium",
    labelKey: "bfm.hasBonusMalusPremium.label",
    name: "bfm.hasBonusMalusPremium.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hasBonusMalusPremium ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.hasBonusMalusPremium && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "hadIndemnityPremium",
    isResizable: true,
    fieldName: "hadIndemnityPremium",
    labelKey: "bfm.hadIndemnityPremium.label",
    name: "bfm.hadIndemnityPremium.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hadIndemnityPremium ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.hadIndemnityPremium && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "hadBonusMalusPremium",
    isResizable: true,
    fieldName: "hadBonusMalusPremium",
    labelKey: "bfm.hadBonusMalusPremium.label",
    name: "bfm.hadBonusMalusPremium.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hadBonusMalusPremium ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.hadBonusMalusPremium && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "hasAttachments",
    isResizable: true,
    fieldName: "hasAttachments",
    labelKey: "bfm.hasAttachments.label",
    name: "bfm.hasAttachments.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hasAttachments ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.hasAttachments && (
        <Icon iconName="Attach" style={{ color: accent.greenDark }} />
      ),
  },

  {
    key: "hullIsBasic",
    isResizable: true,
    fieldName: "hullIsBasic",
    labelKey: "bfm.vehicleList.hullIsBasic.label",
    name: "bfm.vehicleList.hullIsBasic.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.hullIsBasic ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      const value = row?.hullLastStartDate === null ? null : row?.hullIsBasic;
      return <CheckColumn value={value} />;
    },
  },
  {
    key: "hullProduct",
    isResizable: true,
    fieldName: "hullProduct",
    labelKey: "bfm.vehicleForm.hullProduct.label",
    name: "bfm.vehicleForm.hullProduct.label",
    minWidth: 150,
    maxWidth: 200,
    isSortable: true,
    iconName: "Sort",
    isVisible: true,
    filter: {
      type: "taxonomy",
      taxonomyKey: "HullProduct",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },

    onRender: (row) => {
      if (!row) return;

      return (
        <FluentTooltip
          overflowMode={TooltipOverflowMode.Parent}
          content={row?.hullProduct}
        >
          {row?.hullProduct}
        </FluentTooltip>
      );
    },
  },
  {
    key: "vehicleClassCode",
    isResizable: true,
    fieldName: "vehicleClassCode",
    labelKey: "bfm.vehicleForm.vehicleClassCode.label",
    name: "bfm.vehicleForm.vehicleClassCode.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: true,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehicleClass",
      value: [],
    },
    excel: {
      width: 250,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleClass.byId[row?.vehicleClassCode]?.code),
    },
    isSortable: true,
    iconName: "Sort",

    onRender: (row) => {
      if (!row) return;
      // return <VehicleClassCode classCode={row?.vehicleClassCode} />;
      return (
        <CodeCell
          property={"VehicleClass"}
          propertyCode={row?.vehicleClassCode}
        />
      );
    },
  },
  {
    key: "needsGrECoUserAttention",
    isResizable: true,
    fieldName: "needsGrECoUserAttention",
    labelKey: "bfm.vehicle.needsGrECoUserAttention.label",
    name: "bfm.vehicle.needsGrECoUserAttention.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: true,
    filter: {
      type: "boolean",
      value: null,
    },
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) =>
        row?.needsGrECoUserAttention ? t("greco.yes") : "",
      isSortable: false,
      isResizable: true,
    },
    onRender: (row) => {
      return (
        row?.needsGrECoUserAttention && (
          <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
        )
      );
    },
  },
  {
    key: "vin",
    isResizable: true,
    fieldName: "vin",
    labelKey: "bfm.vehicleVin.label",
    name: "bfm.vehicleVin.label",
    minWidth: 150,
    maxWidth: 200,
    isSortable: true,
    iconName: "Sort",
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "vehicleMainGroupCode",
    isResizable: true,
    fieldName: "vehicleMainGroupCode",
    labelKey: "bfm.vehicleForm.vehicleMainGroupCode.label",
    name: "bfm.vehicleForm.vehicleMainGroupCode.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehicleMainGroup",
      value: [],
    },
    excel: {
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleMainGroup.byId[row?.vehicleMainGroupCode]?.code),
    },
    onRender: (row) => {
      return <VehicleVin vehicleVin={row?.vehicleMainGroupCode} />;
    },
  },
  {
    key: "vehicleTypeCode",
    isResizable: true,
    fieldName: "vehicleTypeCode",
    labelKey: "bfm.vehicleType.label",
    name: "bfm.vehicleType.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehicleTypeLegal",
      value: [],
    },
    excel: {
      width: 300,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleTypeLegal.byId[row?.vehicleTypeCode]?.code),
    },
    onRender: (row) => {
      return (
        <CodeCell
          property={"VehicleTypeLegal"}
          propertyCode={row?.vehicleTypeCode}
        />
      );
    },
  },
  {
    key: "brandCode",
    isResizable: true,
    fieldName: "brandCode",
    labelKey: "bfm.vehicleBrand.label",
    name: "bfm.vehicleBrand.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehicleBrand",
      value: [],
    },
    excel: {
      width: 300,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.VehicleBrand.byId[row?.brandCode]?.code),
    },
    onRender: (row) => {
      // return <BrandCode brandCode={row?.brandCode} />;
      return (
        <CodeCell property={"VehicleBrand"} propertyCode={row?.brandCode} />
      );
    },
  },
  {
    key: "model",
    isResizable: true,
    fieldName: "model",
    labelKey: "bfm.vehicleModel.label",
    name: "bfm.vehicleModel.label",
    minWidth: 170,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "costCenterClient",
    isResizable: true,
    fieldName: "costCenterClient",
    labelKey: "bfm.vehicleForm.costCenterClient.label",
    name: "bfm.vehicleForm.costCenterClient.label",
    minWidth: 200,
    maxWidth: 200,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
  },
  {
    key: "leasingCompany",
    isResizable: true,
    fieldName: "leasingCompany",
    labelKey: "bfm.vehicleForm.leasingCompany.label",
    name: "bfm.vehicleForm.leasingCompany.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.leasingCompany}
      >
        {row?.leasingCompany}
      </FluentTooltip>
    ),
  },
  // {
  //   key: "leasingContractNumber",
  //   isResizable: true,
  //   fieldName: "leasingContractNumber",
  //   labelKey: "bfm.vehicleForm.leasingContractNumber.label",
  //   name: "bfm.vehicleForm.leasingContractNumber.label",
  //   minWidth: 200,
  //   maxWidth: 200,
  //   isVisible: false,
  //   isSortable: true,
  //   iconName: "Sort",
  //   onRender: (row) => (
  //     <FluentTooltip
  //       overflowMode={TooltipOverflowMode.Parent}
  //       content={row?.leasingContractNumber}
  //     >
  //       {row?.leasingContractNumber}
  //     </FluentTooltip>
  //   ),
  // },
  // {
  //   key: "leasingContractStartDate",
  //   isResizable: true,
  //   fieldName: "leasingContractStartDate",
  //   labelKey: "bfm.vehicleForm.leasingContractStartDate.label",
  //   name: "bfm.vehicleForm.leasingContractStartDate.label",
  //   minWidth: 200,
  //   maxWidth: 200,
  //   isVisible: false,
  //   isSortable: true,
  //   iconName: "Sort",
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   excel: {
  //     render: ({ row }) =>
  //       row?.leasingContractStartDate
  //         ? formatDateString(row?.leasingContractStartDate)
  //         : "",
  //   },
  //   onRender: (row) => {
  //     return row?.leasingContractStartDate
  //       ? formatDateString(row?.leasingContractStartDate)
  //       : "";
  //   },
  // },
  // {
  //   key: "leasingContractEndDate",
  //   isResizable: true,
  //   fieldName: "leasingContractEndDate",
  //   labelKey: "bfm.vehicleForm.leasingContractEndDate.label",
  //   name: "bfm.vehicleForm.leasingContractEndDate.label",
  //   minWidth: 200,
  //   maxWidth: 200,
  //   isVisible: false,
  //   isSortable: true,
  //   iconName: "Sort",
  //   filter: {
  //     type: "date",
  //     date1: null,
  //     date2: null,
  //     operator: "isAfter",
  //   },
  //   excel: {
  //     render: ({ row }) =>
  //       row?.leasingContractEndDate
  //         ? formatDateString(row?.leasingContractEndDate)
  //         : "",
  //   },
  //   onRender: (row) => {
  //     return row?.leasingContractEndDate
  //       ? formatDateString(row?.leasingContractEndDate)
  //       : "";
  //   },
  // },
  // {
  //   key: "leasingAmountPaidByLessee",
  //   isResizable: true,
  //   fieldName: "leasingAmountPaidByLessee",
  //   labelKey: "bfm.vehicleForm.leasingAmountPaidByLessee.label",
  //   name: "bfm.vehicleForm.leasingAmountPaidByLessee.label",
  //   minWidth: 150,
  //   maxWidth: 200,
  //   isVisible: false,
  //   isSortable: true,
  //   iconName: "Sort",
  //   filter: {
  //     type: "number",
  //     number1: null,
  //     number2: null,
  //     operator: "gt",
  //   },
  //   excel: {
  //     render: ({ row }) => {
  //       if (!row || row?.leasingAmountPaidByLessee === null) return null;
  //       return formatNumber(row.leasingAmountPaidByLessee);
  //     },
  //   },
  //   onRender: (row) => {
  //     if (!row || row?.leasingAmountPaidByLessee === null) return null;
  //     return formatNumber(row.leasingAmountPaidByLessee);
  //   },
  // },
  // {
  //   key: "eurotaxCode",
  //   isResizable: true,
  //   fieldName: "eurotaxCode",
  //   labelKey: "bfm.vehicleForm.eurotaxCode.label",
  //   name: "bfm.vehicleForm.eurotaxCode.label",
  //   minWidth: 200,
  //   maxWidth: 200,
  //   isVisible: false,
  //   isSortable: true,
  //   iconName: "Sort",
  //   onRender: (row) => (
  //     <FluentTooltip
  //       overflowMode={TooltipOverflowMode.Parent}
  //       content={row?.eurotaxCode}
  //     >
  //       {row?.eurotaxCode}
  //     </FluentTooltip>
  //   ),
  // },

  // {
  //   key: "bonusMalusCode",
  //   isResizable: true,
  //   fieldName: "bonusMalusCode",
  //   labelKey: "bfm.vehicleForm.bonusMalusCode.label",
  //   isSortable: true,
  //   name: "bfm.vehicleForm.bonusMalusCode.label",
  //   minWidth: 150,
  //   iconName: "Sort",
  //   maxWidth: 150,
  //   isVisible: false,
  //   placeholder: "bfm.columnOptions.typeToStartSearching",
  //   filter: {
  //     type: "taxonomy",
  //     isSelect: true,
  //     taxonomyKey: "VehicleBonusMalusCode",
  //     value: [],
  //   },
  //   excel: {
  //     width: 200,
  //     render: ({ t, taxonomy, row }) =>
  //       t(taxonomy.VehicleBonusMalusCode.byId[row?.bonusMalusCode]?.code),
  //     isSortable: false,
  //   },
  //   onRender: (row) => {
  //     if (!row) return;
  //     return (
  //       <CodeCell
  //         property={"VehicleBonusMalusCode"}
  //         propertyCode={row?.bonusMalusCode}
  //       />
  //     );
  //   },
  // },
  {
    key: "clientCoveragesInfo",
    isResizable: true,
    fieldName: "clientCoveragesInfo",
    labelKey: "bfm.coverages.label",
    name: "bfm.coverages.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <ClientCommentJsonDisplay comment={row?.clientCoveragesInfo} />
    ),
  },
  {
    key: "clientComment",
    isResizable: true,
    fieldName: "clientComment",
    labelKey: "bfm.vehicleForm.clientComment.label",
    name: "bfm.vehicleForm.clientComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.clientComment}
      >
        {row?.clientComment}
      </FluentTooltip>
    ),
  },
  {
    key: "seats",
    isResizable: true,
    fieldName: "seats",
    isSortable: true,
    iconName: "Sort",
    labelKey: "bfm.vehicleForm.seats.label",
    name: "bfm.vehicleForm.seats.label",
    minWidth: 100,
    maxWidth: 100,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
      max: 100,
    },
  },
  {
    key: "powerKW",
    isResizable: true,
    fieldName: "powerKW",
    labelKey: "bfm.vehicleForm.powerKw.label",
    name: "bfm.vehicleForm.powerKw.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
  },
  {
    key: "cubicCapacityCcm",
    isResizable: true,
    fieldName: "cubicCapacityCcm",
    labelKey: "bfm.vehicleForm.cubicCapacityCcm.label",
    name: "bfm.vehicleForm.cubicCapacityCcm.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
  },
  {
    key: "weightCapacityKg",
    isResizable: true,
    fieldName: "weightCapacityKg",
    labelKey: "bfm.vehicleForm.weightCapacityKg.label",
    name: "bfm.vehicleForm.weightCapacityKg.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
  },
  {
    key: "maxGrossWeightKg",
    isResizable: true,
    fieldName: "maxGrossWeightKg",
    labelKey: "bfm.vehicleForm.maxGrossWeightKg.label",
    name: "bfm.vehicleForm.maxGrossWeightKg.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
  },
  {
    key: "emptyWeightKg",
    isResizable: true,
    fieldName: "emptyWeightKg",
    labelKey: "bfm.vehicleForm.emptyWeightKg.label",
    name: "bfm.vehicleForm.emptyWeightKg.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
  },
  {
    key: "yearOfConstruction",
    isResizable: true,
    fieldName: "yearOfConstruction",
    labelKey: "bfm.vehicleForm.yearOfConstruction.label",
    name: "bfm.vehicleForm.yearOfConstruction.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      //includeNumber1: true,
      format: "integer",
      includeThousandSeparators: false,
      max: 2100,
    },
  },
  {
    key: "firstRegistrationDate",
    isResizable: true,
    fieldName: "firstRegistrationDate",
    labelKey: "bfm.vehicleForm.firstRegistrationDate.label",
    name: "bfm.vehicleForm.firstRegistrationDate.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isAfter",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.firstRegistrationDate
          ? new Date(row?.firstRegistrationDate)
          : null,
    },
    onRender: (row) => {
      return row?.firstRegistrationDate
        ? formatDateString(row?.firstRegistrationDate)
        : "";
    },
  },
  {
    key: "listPrice",
    isResizable: true,
    fieldName: "listPrice",
    labelKey: "bfm.vehicleForm.listPrice.label",
    name: "bfm.vehicleForm.listPrice.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      // includeNumber1: true,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.listPrice === null) return null;
        return parseFloat(row.listPrice);
      },
    },
    onRender: (row) => {
      if (!row || row?.listPrice === null) return null;
      const parsedNumber = parseFloat(row.listPrice);
      return formatNumber(parsedNumber);
    },
  },
  {
    key: "listPriceTypeCode",
    isResizable: true,
    fieldName: "listPriceTypeCode",
    labelKey: "bfm.vehicleForm.listPriceTypeCode.label",
    name: "bfm.vehicleForm.listPriceTypeCode.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "ListPriceType",
      value: [],
    },
    excel: {
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.ListPriceType.byId[row?.listPriceTypeCode]?.code),
    },
    onRender: (row) => (
      <CodeCell
        property={"ListPriceType"}
        propertyCode={row?.listPriceTypeCode}
      />
    ),
  },
  {
    key: "specialEquipment",
    isResizable: true,
    fieldName: "specialEquipment",
    labelKey: "bfm.vehicleForm.specialEquipment.label",
    name: "bfm.vehicleForm.specialEquipment.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.specialEquipment === null) return null;
        return parseFloat(row.specialEquipment);
      },
    },
    onRender: (row) => {
      if (!row || row?.specialEquipment === null) return null;
      const parsedNumber = parseFloat(row.specialEquipment);
      return formatNumber(parsedNumber);
    },
  },
  {
    key: "secondLicensePlate",
    isResizable: true,
    fieldName: "secondLicensePlate",
    labelKey: "bfm.vehicleForm.secondLicensePlate.label",
    name: "bfm.vehicleForm.secondLicensePlate.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
  },
  {
    key: "vehicleOwner",
    isResizable: true,
    fieldName: "vehicleOwner",
    labelKey: "bfm.vehicleForm.vehicleOwner.label",
    isSortable: true,
    iconName: "Sort",
    name: "bfm.vehicleForm.vehicleOwner.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "vehicleOperator",
    isResizable: true,
    fieldName: "vehicleOperator",
    labelKey: "bfm.vehicleForm.vehicleOperator.label",
    isSortable: true,
    iconName: "Sort",
    name: "bfm.vehicleForm.vehicleOperator.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "nationalCodeID",
    isResizable: true,
    fieldName: "nationalCodeID",
    labelKey: "bfm.vehicleForm.nationalCodeId.label",
    isSortable: true,
    iconName: "Sort",
    name: "bfm.vehicleForm.nationalCodeId.label",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
  },
  {
    key: "co2Emission",
    isResizable: true,
    fieldName: "co2Emission",
    labelKey: "bfm.vehicleForm.co2Emission.label",
    name: "bfm.vehicleForm.co2Emission.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 150,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",

      format: "integer",
    },
  },
  {
    key: "certificateNumber",
    isResizable: true,
    fieldName: "certificateNumber",
    labelKey: "bfm.vehicleForm.certificateNumber.label",
    isSortable: true,
    iconName: "Sort",
    name: "bfm.vehicleForm.certificateNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.certificateNumber}
      >
        {row?.certificateNumber}
      </FluentTooltip>
    ),
  },
  {
    key: "comment",
    isResizable: true,
    fieldName: "comment",
    labelKey: "bfm.vehicleForm.comment.label",
    name: "bfm.vehicleForm.comment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.comment}
      >
        {row?.comment}
      </FluentTooltip>
    ),
  },
  {
    key: "horsePowerBasedTaxComment",
    isResizable: true,
    fieldName: "horsePowerBasedTaxComment",
    labelKey: "bfm.vehicleForm.enginepowertaxcomment.label",
    name: "bfm.vehicleForm.enginepowertaxcomment.label",
    minWidth: 150,
    maxWidth: 200,
    isSortable: true,
    iconName: "Sort",
    isVisible: false,
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.horsePowerBasedTaxComment}
      >
        {row?.horsePowerBasedTaxComment}
      </FluentTooltip>
    ),
  },
  {
    key: "mtplComment",
    isResizable: true,
    fieldName: "mtplComment",
    labelKey: "bfm.vehicleForm.mtplcomment.label",
    name: "bfm.vehicleForm.mtplcomment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.mtplComment}
      >
        {row?.mtplComment}
      </FluentTooltip>
    ),
  },

  {
    key: "hullComment",
    isResizable: true,
    fieldName: "hullComment",
    labelKey: "bfm.vehicleForm.hullcomment.label",
    name: "bfm.vehicleForm.hullcomment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.hullComment}
      >
        {row?.hullComment}
      </FluentTooltip>
    ),
  },
  {
    key: "accidentComment",
    isResizable: true,
    fieldName: "accidentComment",
    labelKey: "bfm.vehicleForm.accidentcomment.label",
    name: "bfm.vehicleForm.accidentcomment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.accidentComment}
      >
        {row?.accidentComment}
      </FluentTooltip>
    ),
  },
  {
    key: "assistanceComment",
    isResizable: true,
    fieldName: "assistanceComment",
    labelKey: "bfm.vehicleForm.assistancecomment.label",
    name: "bfm.vehicleForm.assistancecomment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.assistanceComment}
      >
        {row?.assistanceComment}
      </FluentTooltip>
    ),
  },
  {
    key: "legalComment",
    isResizable: true,
    fieldName: "legalComment",
    labelKey: "bfm.vehicleForm.legalcomment.label",
    name: "bfm.vehicleForm.legalcomment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={row?.legalComment}
      >
        {row?.legalComment}
      </FluentTooltip>
    ),
  },
  {
    key: "mtplGreenCardExpiryDate",
    isResizable: true,
    fieldName: "mtplGreenCardExpiryDate",
    labelKey: "bfm.fleetForm.mtplGreenCardExpiryDate.label",
    name: "bfm.fleetForm.mtplGreenCardExpiryDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.mtplGreenCardExpiryDate
          ? new Date(row?.mtplGreenCardExpiryDate)
          : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplGreenCardExpiryDate
            ? formatDateString(row?.mtplGreenCardExpiryDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "excludeHorsePowerBasedTaxReason",
    isResizable: true,
    fieldName: "excludeHorsePowerBasedTaxReason",
    labelKey: "bfm.vehicleForm.excludeHorsePowerBasedTaxReason.label",
    name: "bfm.vehicleForm.excludeHorsePowerBasedTaxReason.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      taxonomyKey: "ReasonForMotorTaxExclusion",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.ReasonForMotorTaxExclusion.byId[
            row?.excludeHorsePowerBasedTaxReason
          ]?.code,
        ),
    },
    onRender: (row) => (
      <ExcludeHorsePowerBasedTaxReason
        code={row?.excludeHorsePowerBasedTaxReason}
      />
    ),
  },
  {
    key: "excludeHorsePowerBasedTax",
    isResizable: true,
    fieldName: "excludeHorsePowerBasedTax",
    labelKey: "bfm.vehicleForm.excludeHorsePowerBasedTax.label",
    name: "bfm.vehicleForm.excludeHorsePowerBasedTax.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) =>
        row?.excludeHorsePowerBasedTax ? t("greco.yes") : "",
    },
    onRender: (row) =>
      row?.excludeHorsePowerBasedTax && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "gap",
    isResizable: true,
    fieldName: "gap",
    labelKey: "bfm.vehicleForm.gap.label",
    name: "bfm.vehicleForm.gap.label",
    minWidth: 80,
    maxWidth: 80,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.gap ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.gap && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "replacementValue",
    isResizable: true,
    fieldName: "replacementValue",
    labelKey: "bfm.vehicleForm.replacementValue.label",
    name: "bfm.vehicleForm.replacementValue.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.replacementValue ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.replacementValue && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "grossNegligence",
    isResizable: true,
    fieldName: "grossNegligence",
    labelKey: "bfm.vehicleForm.grossNegligence.label",
    name: "bfm.vehicleForm.grossNegligence.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.grossNegligence ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.grossNegligence && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "electroPlusPackage",
    isResizable: true,
    fieldName: "electroPlusPackage",
    labelKey: "bfm.vehicleForm.electroPlusPackage.label",
    name: "bfm.vehicleForm.electroPlusPackage.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.electroPlusPackage ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.electroPlusPackage && (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ),
  },
  {
    key: "horsePowerBasedTaxAnnualy",
    isResizable: true,
    fieldName: "horsePowerBasedTaxAnnualy",
    labelKey: "bfm.vehicleForm.horsePowerBasedTaxAnnualy.label",
    name: "bfm.vehicleForm.horsePowerBasedTaxAnnualy.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.horsePowerBasedTaxAnnualy === null) return null;
        return row.horsePowerBasedTaxAnnualy;
      },
    },
    onRender: (row) => {
      if (!row || row?.horsePowerBasedTaxAnnualy === null) return null;
      return formatNumber(row.horsePowerBasedTaxAnnualy);
    },
  },
  {
    key: "bookingAmountEngineTax",
    isResizable: true,
    fieldName: "bookingAmountEngineTax",
    labelKey: "bfm.bookingAmountEngineTax.label",
    name: "bfm.bookingAmountEngineTax.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.horsePowerBasedTaxAnnualy, taxonomy, row?.horsePowerBasedTaxPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.horsePowerBasedTaxAnnualy}
        paymentMethodCode={row?.horsePowerBasedTaxPaymentMethod}
      />
    ),
  },
  {
    key: "horsePowerBasedTaxPaymentMethod",
    isResizable: true,
    fieldName: "horsePowerBasedTaxPaymentMethod",
    labelKey: "bfm.horsePowerBasedTaxPaymentMethod.label",
    isSortable: true,
    name: "bfm.horsePowerBasedTaxPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.horsePowerBasedTaxPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.horsePowerBasedTaxPaymentMethod}
        />
      );
    },
  },
  {
    key: "horsePowerBasedTaxProRata",
    isResizable: true,
    fieldName: "horsePowerBasedTaxProRata",
    labelKey: "bfm.vehicleForm.horsePowerBasedTaxProRata.label",
    name: "bfm.vehicleForm.horsePowerBasedTaxProRata.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.horsePowerBasedTaxProRata === null) return null;
        return row.horsePowerBasedTaxProRata;
      },
    },
    onRender: (row) => {
      if (!row || row?.horsePowerBasedTaxProRata === null) return null;
      return formatNumber(row.horsePowerBasedTaxProRata);
    },
  },
  {
    key: "hullDeductibleComment",
    isResizable: true,
    fieldName: "hullDeductibleComment",
    labelKey: "bfm.vehicleForm.hullDeductibleComment.label",
    name: "bfm.vehicleForm.hullDeductibleComment.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
  },
  {
    key: "hullDeductibleMaxAmount",
    isResizable: true,
    fieldName: "hullDeductibleMaxAmount",
    labelKey: "bfm.vehicleForm.hullDeductibleMaxAmount.label",
    name: "bfm.vehicleForm.hullDeductibleMaxAmount.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullDeductibleMaxAmount === null) return null;
        return row.hullDeductibleMaxAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullDeductibleMaxAmount === null) return null;
      return formatNumber(row.hullDeductibleMaxAmount);
    },
  },
  {
    key: "hullDeductibleMinAmount",
    isResizable: true,
    fieldName: "hullDeductibleMinAmount",
    labelKey: "bfm.vehicleForm.hullDeductibleMinAmount.label",
    name: "bfm.vehicleForm.hullDeductibleMinAmount.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullDeductibleMinAmount === null) return null;
        return row.hullDeductibleMinAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullDeductibleMinAmount === null) return null;
      return formatNumber(row.hullDeductibleMinAmount);
    },
  },
  {
    key: "hullDeductiblePercent",
    isResizable: true,
    fieldName: "hullDeductiblePercent",
    labelKey: "bfm.vehicleForm.hullDeductiblePercent.label",
    name: "bfm.vehicleForm.hullDeductiblePercent.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      format: "integer",
      includeThousandSeparators: false,
    },
    excel: {
      render: ({ t, row }) => {
        if (!row || row?.hullDeductiblePercent === null) return null;
        return `${row.hullDeductiblePercent}%`;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullDeductiblePercent === null) return null;
      return `${row.hullDeductiblePercent}%`;
    },
  },
  {
    key: "hullGroupPolicyNumber",
    isResizable: true,
    fieldName: "hullGroupPolicyNumber",
    labelKey: "bfm.hullGroupPolicyNumber",
    name: "bfm.hullGroupPolicyNumber",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "hullPolicyNumber",
    isResizable: true,
    fieldName: "hullPolicyNumber",
    labelKey: "bfm.hullPolicyNumber",
    name: "bfm.hullPolicyNumber",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "hullIsPolicyIssued",
    isResizable: true,
    fieldName: "hullIsPolicyIssued",
    labelKey: "bfm.fleetForm.hullIsPolicyIssued.label",
    name: "bfm.fleetForm.hullIsPolicyIssued.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) => (row?.hullIsPolicyIssued ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.hullIsPolicyIssued} />;
    },
  },
  {
    key: "hullPolicyDocumentDate",
    isResizable: true,
    fieldName: "hullPolicyDocumentDate",
    labelKey: "bfm.fleetForm.hullPolicyDocumentDate.label",
    name: "bfm.fleetForm.hullPolicyDocumentDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullPolicyDocumentDate
          ? new Date(row?.hullPolicyDocumentDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullPolicyDocumentDate
            ? formatDateString(row?.hullPolicyDocumentDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "isIncludingTax",
    isResizable: true,
    fieldName: "isIncludingTax",
    labelKey: "bfm.vehicleForm.isIncludingTax.label",
    name: "bfm.vehicleForm.isIncludingTax.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.isIncludingTax ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      return <CheckColumn value={row?.isIncludingTax} />;
    },
  },
  {
    key: "isLeasing",
    isResizable: true,
    fieldName: "isLeasing",
    labelKey: "bfm.vehicleForm.isLeasing.label",
    name: "bfm.vehicleForm.isLeasing.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.isLeasing ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      return <CheckColumn value={row?.isLeasing} />;
    },
  },
  {
    key: "legalAnnualGrossPremium",
    isResizable: true,
    fieldName: "legalAnnualGrossPremium",
    labelKey: "bfm.legalAnnualGrossPremium.label",
    name: "bfm.legalAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",

    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.legalAnnualGrossPremium === null) return null;
        return row.legalAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.legalAnnualGrossPremium === null) return null;
      return formatNumber(row.legalAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountLegal",
    isResizable: true,
    fieldName: "bookingAmountLegal",
    labelKey: "bfm.bookingAmountLegal.label",
    name: "bfm.bookingAmountLegal.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.legalAnnualGrossPremium, taxonomy, row?.mtplPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.legalAnnualGrossPremium}
        paymentMethodCode={row?.mtplPaymentMethod}
      />
    ),
  },
  {
    key: "legalProRataGrossPremium",
    isResizable: true,
    fieldName: "legalProRataGrossPremium",
    labelKey: "bfm.legalProRataGrossPremium.label",
    name: "bfm.legalProRataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.legalProRataGrossPremium === null) return null;
        return row.legalProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.legalProRataGrossPremium === null) return null;
      return formatNumber(row.legalProRataGrossPremium);
    },
  },
  {
    key: "mtplAnnualGrossPremium",
    isResizable: true,
    fieldName: "mtplAnnualGrossPremium",
    labelKey: "bfm.mtplAnnualGrossPremium",
    name: "bfm.mtplAnnualGrossPremium",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.mtplAnnualGrossPremium === null) return null;
        return row.mtplAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.mtplAnnualGrossPremium === null) return null;
      return formatNumber(row.mtplAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountMtpl",
    isResizable: true,
    fieldName: "bookingAmountMtpl",
    labelKey: "bfm.bookingAmountMtpl.label",
    name: "bfm.bookingAmountMtpl.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.mtplAnnualGrossPremium, taxonomy, row?.mtplPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.mtplAnnualGrossPremium}
        paymentMethodCode={row?.mtplPaymentMethod}
      />
    ),
  },
  {
    key: "mtplGroupPolicyNumber",
    isResizable: true,
    fieldName: "mtplGroupPolicyNumber",
    labelKey: "bfm.mtplGroupPolicyNumber",
    name: "bfm.mtplGroupPolicyNumber",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "mtplPolicyNumber",
    isResizable: true,
    fieldName: "mtplPolicyNumber",
    labelKey: "bfm.mtplPolicyNumber",
    name: "bfm.mtplPolicyNumber",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "mtplIsPolicyIssued",
    isResizable: true,
    fieldName: "mtplIsPolicyIssued",
    labelKey: "bfm.fleetForm.mtplIsPolicyIssued.label",
    name: "bfm.fleetForm.mtplIsPolicyIssued.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) => (row?.mtplIsPolicyIssued ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.mtplIsPolicyIssued} />;
    },
  },
  {
    key: "mtplPolicyDocumentDate",
    isResizable: true,
    fieldName: "mtplPolicyDocumentDate",
    labelKey: "bfm.fleetForm.mtplPolicyDocumentDate.label",
    name: "bfm.fleetForm.mtplPolicyDocumentDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.mtplPolicyDocumentDate
          ? new Date(row?.mtplPolicyDocumentDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplPolicyDocumentDate
            ? formatDateString(row?.mtplPolicyDocumentDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "mtplProRataGrossPremium",
    isResizable: true,
    fieldName: "mtplProRataGrossPremium",
    labelKey: "bfm.mtplProRataGrossPremium.label",
    name: "bfm.mtplProRataGrossPremium.label",

    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.mtplProRataGrossPremium === null) return null;
        return row.mtplProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.mtplProRataGrossPremium === null) return null;
      return formatNumber(row.mtplProRataGrossPremium);
    },
  },
  {
    key: "accidentAnnualGrossPremium",
    isResizable: true,
    fieldName: "accidentAnnualGrossPremium",
    labelKey: "bfm.accidentAnnualGrossPremium.label",
    name: "bfm.accidentAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      // includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.accidentAnnualGrossPremium === null) return null;
        return row.accidentAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.accidentAnnualGrossPremium === null) return null;
      return formatNumber(row.accidentAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountAccident",
    isResizable: true,
    fieldName: "bookingAmountAccident",
    labelKey: "bfm.bookingAmountAccident.label",
    name: "bfm.bookingAmountAccident.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.accidentAnnualGrossPremium, taxonomy, row?.mtplPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.accidentAnnualGrossPremium}
        paymentMethodCode={row?.mtplPaymentMethod}
      />
    ),
  },
  {
    key: "accidentProRataGrossPremium",
    isResizable: true,
    fieldName: "accidentProRataGrossPremium",
    labelKey: "bfm.accidentProRataGrossPremium.label",
    name: "bfm.accidentProRataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.accidentProRataGrossPremium === null) return null;
        return row.accidentProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.accidentProRataGrossPremium === null) return null;
      return formatNumber(row.accidentProRataGrossPremium);
    },
  },
  {
    key: "assistanceAnnualGrossPremium",
    isResizable: true,
    fieldName: "assistanceAnnualGrossPremium",
    labelKey: "bfm.assistanceAnnualGrossPremium.label",
    name: "bfm.assistanceAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      // format: "integer",
      // includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.assistanceAnnualGrossPremium === null) return null;
        return row.assistanceAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.assistanceAnnualGrossPremium === null) return null;
      return formatNumber(row.assistanceAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountAssistance",
    isResizable: true,
    fieldName: "bookingAmountAssistance",
    labelKey: "bfm.bookingAmountAssistance.label",
    name: "bfm.bookingAmountAssistance.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.assistanceAnnualGrossPremium, taxonomy, row?.mtplPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.assistanceAnnualGrossPremium}
        paymentMethodCode={row?.mtplPaymentMethod}
      />
    ),
  },
  {
    key: "assistanceProRataGrossPremium",
    isResizable: true,
    fieldName: "assistanceProRataGrossPremium",
    labelKey: "bfm.assistanceProRataGrossPremium.label",
    name: "bfm.assistanceProRataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      includeThousandSeparators: false,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.assistanceProRataGrossPremium === null) return null;
        return row.assistanceProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.assistanceProRataGrossPremium === null) return null;
      return formatNumber(row.assistanceProRataGrossPremium);
    },
  },
  {
    key: "vatReimbursment",
    isResizable: true,
    fieldName: "vatReimbursment",
    labelKey: "bfm.vehicleForm.vatReimbursment.label",
    name: "bfm.vehicleForm.vatReimbursment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.vatReimbursment ? t("greco.yes") : ""),
    },
    onRender: (row) => {
      return <CheckColumn value={row?.vatReimbursment} />;
    },
  },
  {
    key: "vehiclePowerTypeCode",
    isResizable: true,
    fieldName: "vehiclePowerTypeCode",
    labelKey: "bfm.vehicleForm.vehiclePowerTypeCode.label",
    name: "bfm.vehicleForm.vehiclePowerTypeCode.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      isSelect: true,
      taxonomyKey: "VehiclePowerType",
      value: [],
      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(taxonomy.VehiclePowerType.byId[row?.vehiclePowerTypeCode]?.code),
    },
    onRender: (row) => {
      return (
        <CodeCell
          property={"VehiclePowerType"}
          propertyCode={row?.vehiclePowerTypeCode}
        />
      );
    },
  },
  {
    key: "isPluginHybrid",
    isResizable: true,
    fieldName: "isPluginHybrid",
    labelKey: "bfm.vehicleForm.isPluginHybrid.label",
    name: "bfm.vehicleForm.isPluginHybrid.label",
    minWidth: 120,
    maxWidth: 120,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) => (row?.isPluginHybrid ? t("greco.yes") : ""),
    },
    onRender: (row) =>
      row?.isPluginHybrid === true ? (
        <Icon iconName="SkypeCheck" style={{ color: accent.greenDark }} />
      ) : row?.isPluginHybrid === false ? (
        <Icon iconName="Cancel" style={{ color: accent.redDark }} />
      ) : null,
  },
  {
    key: "vehicleIssuingComment",
    isResizable: true,
    fieldName: "vehicleIssuingComment",
    labelKey: "bfm.vehicleForm.vehicleIssuingComment.label",
    name: "bfm.vehicleForm.vehicleIssuingComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
  },
  {
    key: "vehicleIssuingSubstatusCode",
    isResizable: true,
    fieldName: "vehicleIssuingSubstatusCode",
    labelKey: "bfm.issuingSubstatus",
    name: "bfm.issuingSubstatus",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      taxonomyKey: "VehicleIssuingSubstatus",
      value: [],

      renderLabel: ({ taxonomy, t }) => {
        return (
          <>
            <Truncate>{t(taxonomy?.code)}</Truncate>
          </>
        );
      },
    },
    excel: {
      render: ({ row, t, taxonomy }) =>
        t(
          taxonomy.VehicleIssuingSubstatus.byId[
            row?.vehicleIssuingSubstatusCode
          ]?.code,
        ),
    },
    onRender: (row) => {
      return (
        <CodeCell
          property={"VehicleIssuingSubstatus"}
          propertyCode={row?.vehicleIssuingSubstatusCode}
        />
      );
    },
  },
  {
    key: "mtplInsurerPartnerNumber",
    isResizable: true,
    fieldName: "mtplInsurerPartnerNumber",
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    labelKey: "bfm.vehicleForm.mtplInsurerPartnerNumber.label",
    name: "bfm.vehicleForm.mtplInsurerPartnerNumber.label",
    minWidth: 200,
    maxWidth: 300,
    filter: {
      type: "taxonomy",
      isSelect: true,
      value: [],
      taxonomyKey: "mtplInsurerPartnerNumber",
      renderLabel: ({ taxonomy, t }: any) => {
        return <>{t(taxonomy.code)}</>;
      },
    },
    onRender: (row) => {
      if (!row) return null;
      return (
        <ColumnHoverCard vehicle={row}>
          <LineInsurer partnerNumber={row?.mtplInsurerPartnerNumber} />
        </ColumnHoverCard>
      );
    },
    excel: {
      render: ({ row, t, insurerList }) => {
        const insurer1 = insurerList.find(
          (insurer) =>
            insurer.insurerInternalNumber + "" === row.mtplInsurerPartnerNumber,
        );
        const insurerName1 = insurer1 ? insurer1?.insurerName : "";
        return insurerName1;
      },
    },
  },
  {
    key: "mtplExpiryDate",
    isResizable: true,
    fieldName: "mtplExpiryDate",
    labelKey: "bfm.fleetForm.mtplExpiryDate.label",
    name: "bfm.fleetForm.mtplExpiryDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.mtplExpiryDate ? new Date(row?.mtplExpiryDate) : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplExpiryDate ? formatDateString(row?.mtplExpiryDate) : null}
        </span>
      );
    },
  },
  {
    key: "mtplLastStartDate",
    isResizable: true,
    fieldName: "mtplLastStartDate",
    labelKey: "bfm.mtplLastStartDate.label",
    name: "bfm.mtplLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.mtplLastStartDate ? new Date(row?.mtplLastStartDate) : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplLastStartDate
            ? formatDateString(row?.mtplLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "mtplImportEndDate",
    isResizable: true,
    fieldName: "mtplImportEndDate",
    labelKey: "bfm.mtplImportEndDate.label",
    name: "bfm.mtplImportEndDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.mtplImportEndDate ? new Date(row?.mtplImportEndDate) : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.mtplImportEndDate
            ? formatDateString(row?.mtplImportEndDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullInsurerPartnerNumber",
    isResizable: true,
    fieldName: "hullInsurerPartnerNumber",
    isVisible: true,
    labelKey: "bfm.vehicleForm.hullInsurerPartnerNumber.label",
    name: "bfm.vehicleForm.hullInsurerPartnerNumber.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 300,
    filter: {
      type: "taxonomy",
      isSelect: true,
      value: [],
      taxonomyKey: "hullInsurerPartnerNumber",
      renderLabel: ({ taxonomy, t }: any) => {
        return <>{t(taxonomy.code)}</>;
      },
    },

    onRender: (row) => {
      if (!row) return null;
      return (
        <ColumnHoverCard vehicle={row}>
          <LineInsurer partnerNumber={row?.hullInsurerPartnerNumber} />
        </ColumnHoverCard>
      );
    },
    excel: {
      render: ({ row, t, insurerList }) => {
        const insurer1 = insurerList.find(
          (insurer) =>
            insurer.insurerInternalNumber + "" === row.hullInsurerPartnerNumber,
        );
        const insurerName1 = insurer1 ? insurer1?.insurerName : "";
        return insurerName1;
      },
    },
  },
  {
    key: "hullExpiryDate",
    isResizable: true,
    fieldName: "hullExpiryDate",
    labelKey: "bfm.fleetForm.hullExpiryDate.label",
    name: "bfm.fleetForm.hullExpiryDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullExpiryDate ? new Date(row?.hullExpiryDate) : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullExpiryDate ? formatDateString(row?.hullExpiryDate) : null}
        </span>
      );
    },
  },
  {
    key: "hullLastStartDate",
    isResizable: true,
    fieldName: "hullLastStartDate",
    labelKey: "bfm.hullLastStartDate.label",
    name: "bfm.hullLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullLastStartDate ? new Date(row?.hullLastStartDate) : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullLastStartDate
            ? formatDateString(row?.hullLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullImportEndDate",
    isResizable: true,
    fieldName: "hullImportEndDate",
    labelKey: "bfm.hullImportEndDate.label",
    name: "bfm.hullImportEndDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullImportEndDate ? new Date(row?.hullImportEndDate) : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullImportEndDate
            ? formatDateString(row?.hullImportEndDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "accidentLastStartDate",
    isResizable: true,
    fieldName: "accidentLastStartDate",
    labelKey: "bfm.accidentLastStartDate.label",
    name: "bfm.accidentLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.accidentLastStartDate
          ? new Date(row?.accidentLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.accidentLastStartDate
            ? formatDateString(row?.accidentLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "assistanceLastStartDate",
    isResizable: true,
    fieldName: "assistanceLastStartDate",
    labelKey: "bfm.assistanceLastStartDate.label",
    name: "bfm.assistanceLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.assistanceLastStartDate
          ? new Date(row?.assistanceLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.assistanceLastStartDate
            ? formatDateString(row?.assistanceLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "legalLastStartDate",
    isResizable: true,
    fieldName: "legalLastStartDate",
    labelKey: "bfm.legalLastStartDate.label",
    name: "bfm.legalLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.legalLastStartDate ? new Date(row?.legalLastStartDate) : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.legalLastStartDate
            ? formatDateString(row?.legalLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "horsePowerBasedTaxLastStartDate",
    isResizable: true,
    fieldName: "horsePowerBasedTaxLastStartDate",
    labelKey: "bfm.horsePowerBasedTaxLastStartDate.label",
    name: "bfm.horsePowerBasedTaxLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.horsePowerBasedTaxLastStartDate
          ? new Date(row?.horsePowerBasedTaxLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.horsePowerBasedTaxLastStartDate
            ? formatDateString(row?.horsePowerBasedTaxLastStartDate)
            : null}
        </span>
      );
    },
  },
  //fleet legal
  {
    key: "fleetLegalGroupPolicyNumber",
    isResizable: true,
    fieldName: "fleetLegalGroupPolicyNumber",
    labelKey: "bfm.fleetLegalGroupPolicyNumber",
    name: "bfm.fleetLegalGroupPolicyNumber",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "fleetLegalPolicyNumber",
    isResizable: true,
    fieldName: "fleetLegalPolicyNumber",
    labelKey: "bfm.fleetForm.fleetLegalPolicyNumber.label",
    name: "bfm.fleetForm.fleetLegalPolicyNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "fleetLegalIsPolicyIssued",
    isResizable: true,
    fieldName: "fleetLegalIsPolicyIssued",
    labelKey: "bfm.fleetForm.fleetLegalIsPolicyIssued.label",
    name: "bfm.fleetForm.fleetLegalIsPolicyIssued.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) =>
        row?.fleetLegalIsPolicyIssued ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.fleetLegalIsPolicyIssued} />;
    },
  },
  {
    key: "fleetLegalPolicyDocumentDate",
    isResizable: true,
    fieldName: "fleetLegalPolicyDocumentDate",
    labelKey: "bfm.fleetForm.fleetLegalPolicyDocumentDate.label",
    name: "bfm.fleetForm.fleetLegalPolicyDocumentDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.fleetLegalPolicyDocumentDate
          ? new Date(row?.fleetLegalPolicyDocumentDate)
          : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.fleetLegalPolicyDocumentDate
            ? formatDateString(row?.fleetLegalPolicyDocumentDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "fleetLegalInsurerPartnerNumber",
    isResizable: true,
    fieldName: "fleetLegalInsurerPartnerNumber",
    labelKey: "bfm.fleetForm.fleetLegalInsurerPartnerNumber.label",
    name: "bfm.fleetForm.fleetLegalInsurerPartnerNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      isSelect: true,
      value: [],
      taxonomyKey: "fleetLegalInsurerPartnerNumber",
      renderLabel: ({ taxonomy, t }: any) => {
        return <>{t(taxonomy.code)}</>;
      },
    },
    onRender: (row) => {
      if (!row) return null;
      return (
        <ColumnHoverCard vehicle={row}>
          <LineInsurer partnerNumber={row?.fleetLegalInsurerPartnerNumber} />
        </ColumnHoverCard>
      );
    },
    excel: {
      render: ({ row, t, insurerList }) => {
        const insurer1 = insurerList.find(
          (insurer) =>
            insurer.insurerInternalNumber + "" ===
            row.fleetLegalInsurerPartnerNumber,
        );
        const insurerName1 = insurer1 ? insurer1?.insurerName : "";
        return insurerName1;
      },
    },
  },
  {
    key: "fleetLegalAnnualGrossPremium",
    isResizable: true,
    fieldName: "fleetLegalAnnualGrossPremium",
    labelKey: "bfm.fleetLegalProRataAnnualGrossPremium.label",
    name: "bfm.fleetLegalProRataAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.fleetLegalAnnualGrossPremium === null) return null;
        return row?.fleetLegalAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.fleetLegalAnnualGrossPremium === null) return null;
      return formatNumber(row.fleetLegalAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountFleetLegal",
    isResizable: true,
    fieldName: "bookingAmountFleetLegal",
    labelKey: "bfm.bookingAmountFleetLegal.label",
    name: "bfm.bookingAmountFleetLegal.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.fleetLegalAnnualGrossPremium, taxonomy, row?.fleetLegalPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.fleetLegalAnnualGrossPremium}
        paymentMethodCode={row?.fleetLegalPaymentMethod}
      />
    ),
  },
  {
    key: "fleetLegalPaymentMethod",
    isResizable: true,
    fieldName: "fleetLegalPaymentMethod",
    labelKey: "bfm.fleetLegalPaymentMethod.label",
    isSortable: true,
    name: "bfm.fleetLegalPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.fleetLegalPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.fleetLegalPaymentMethod}
        />
      );
    },
  },
  {
    key: "fleetLegalProRataGrossPremium",
    isResizable: true,
    fieldName: "fleetLegalProRataGrossPremium",
    labelKey: "bfm.fleetLegalProrataGrossPremium.label",
    name: "bfm.fleetLegalProrataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.fleetLegalProRataGrossPremium === null) return null;
        return row?.fleetLegalProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.fleetLegalProRataGrossPremium === null) return null;
      return formatNumber(row.fleetLegalProRataGrossPremium);
    },
  },
  {
    key: "fleetLegalIsClosingDate",
    isResizable: true,
    fieldName: "fleetLegalIsClosingDate",
    labelKey: "bfm.fleetLegalIsClosingDate.label",
    name: "bfm.fleetLegalIsClosingDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",

    excel: {
      render: ({ t, row }) =>
        row?.fleetLegalIsClosingDate ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.fleetLegalIsClosingDate} />;
    },
  },
  {
    key: "fleetLegalLastStartDate",
    isResizable: true,
    fieldName: "fleetLegalLastStartDate",
    labelKey: "bfm.fleetLegalLastStartDate.label",
    name: "bfm.fleetLegalLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.fleetLegalLastStartDate
          ? new Date(row?.fleetLegalLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.fleetLegalLastStartDate
            ? formatDateString(row?.fleetLegalLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "fleetLegalImportEndDate",
    isResizable: true,
    fieldName: "fleetLegalImportEndDate",
    labelKey: "bfm.fleetLegalImportEndDate.label",
    name: "bfm.fleetLegalImportEndDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.fleetLegalImportEndDate
          ? new Date(row?.fleetLegalImportEndDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.fleetLegalImportEndDate
            ? formatDateString(row?.fleetLegalImportEndDate)
            : null}
        </span>
      );
    },
  },

  {
    key: "fleetLegalComment",
    isResizable: true,
    fieldName: "fleetLegalComment",
    labelKey: "bfm.fleetLegalComment.label",
    name: "bfm.fleetLegalComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
  },

  {
    key: "fleetLegalRelatedBookingAmount",
    isResizable: true,
    fieldName: "fleetLegalRelatedBookingAmount",
    labelKey: "bfm.fleetLegalRelatedBookingAmount.label",
    name: "bfm.fleetLegalRelatedBookingAmount.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.fleetLegalRelatedBookingAmount === null) return null;
        return row?.fleetLegalRelatedBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.fleetLegalRelatedBookingAmount === null) return null;
      return formatNumber(row.fleetLegalRelatedBookingAmount);
    },
  },

  {
    key: "hullOnLandPolicyNumber",
    isResizable: true,
    fieldName: "hullOnLandPolicyNumber",
    labelKey: "bfm.fleetForm.hullOnLandPolicyNumber.label",
    name: "bfm.fleetForm.hullOnLandPolicyNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "hullOnLandIsPolicyIssued",
    isResizable: true,
    fieldName: "hullOnLandIsPolicyIssued",
    labelKey: "bfm.fleetForm.hullOnLandIsPolicyIssued.label",
    name: "bfm.fleetForm.hullOnLandIsPolicyIssued.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) =>
        row?.hullOnLandIsPolicyIssued ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.hullOnLandIsPolicyIssued} />;
    },
  },
  {
    key: "hullOnLandPolicyDocumentDate",
    isResizable: true,
    fieldName: "hullOnLandPolicyDocumentDate",
    labelKey: "bfm.fleetForm.hullOnLandPolicyDocumentDate.label",
    name: "bfm.fleetForm.hullOnLandPolicyDocumentDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullOnLandPolicyDocumentDate
          ? new Date(row?.hullOnLandPolicyDocumentDate)
          : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullOnLandPolicyDocumentDate
            ? formatDateString(row?.hullOnLandPolicyDocumentDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullOnLandInsurerPartnerNumber",
    isResizable: true,
    fieldName: "hullOnLandInsurerPartnerNumber",
    labelKey: "bfm.fleetForm.hullOnLandInsurerPartnerNumber.label",
    name: "bfm.fleetForm.hullOnLandInsurerPartnerNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      isSelect: true,
      value: [],
      taxonomyKey: "hullOnLandInsurerPartnerNumber",
      renderLabel: ({ taxonomy, t }: any) => {
        return <>{t(taxonomy.code)}</>;
      },
    },
    onRender: (row) => {
      if (!row) return null;
      return (
        <ColumnHoverCard vehicle={row}>
          <LineInsurer partnerNumber={row?.hullOnLandInsurerPartnerNumber} />
        </ColumnHoverCard>
      );
    },
    excel: {
      render: ({ row, t, insurerList }) => {
        const insurer1 = insurerList.find(
          (insurer) =>
            insurer.insurerInternalNumber + "" ===
            row.hullOnLandInsurerPartnerNumber,
        );
        const insurerName1 = insurer1 ? insurer1?.insurerName : "";
        return insurerName1;
      },
    },
  },
  {
    key: "hullOnLandAnnualGrossPremium",
    isResizable: true,
    fieldName: "hullOnLandAnnualGrossPremium",
    labelKey: "bfm.hullOnLandProRataAnnualGrossPremium.label",
    name: "bfm.hullOnLandProRataAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullOnLandAnnualGrossPremium === null) return null;
        return row?.hullOnLandAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullOnLandAnnualGrossPremium === null) return null;
      return formatNumber(row.hullOnLandAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountHullonLand",
    isResizable: true,
    fieldName: "bookingAmountHullonLand",
    labelKey: "bfm.bookingAmountHullonLand.label",
    name: "bfm.bookingAmountHullonLand.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.hullOnLandAnnualGrossPremium, taxonomy, row?.hullOnLandPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.hullOnLandAnnualGrossPremium}
        paymentMethodCode={row?.hullOnLandPaymentMethod}
      />
    ),
  },
  {
    key: "hullOnLandPaymentMethod",
    isResizable: true,
    fieldName: "hullOnLandPaymentMethod",
    labelKey: "bfm.hullOnLandPaymentMethod.label",
    isSortable: true,
    name: "bfm.hullOnLandPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.hullOnLandPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.hullOnLandPaymentMethod}
        />
      );
    },
  },
  {
    key: "hullOnLandProRataGrossPremium",
    isResizable: true,
    fieldName: "hullOnLandProRataGrossPremium",
    labelKey: "bfm.hullOnLandProrataGrossPremium.label",
    name: "bfm.hullOnLandProrataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullOnLandProRataGrossPremium === null) return null;
        return row?.hullOnLandProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullOnLandProRataGrossPremium === null) return null;
      return formatNumber(row.hullOnLandProRataGrossPremium);
    },
  },
  {
    key: "hullOnLandIsClosingDate",
    isResizable: true,
    fieldName: "hullOnLandIsClosingDate",
    labelKey: "bfm.hullOnLandIsClosingDate.label",
    name: "bfm.hullOnLandIsClosingDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",

    excel: {
      render: ({ t, row }) =>
        row?.hullOnLandIsClosingDate ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.hullOnLandIsClosingDate} />;
    },
  },
  {
    key: "hullOnLandLastStartDate",
    isResizable: true,
    fieldName: "hullOnLandLastStartDate",
    labelKey: "bfm.hullOnLandLastStartDate.label",
    name: "bfm.hullOnLandLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullOnLandLastStartDate
          ? new Date(row?.hullOnLandLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullOnLandLastStartDate
            ? formatDateString(row?.hullOnLandLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "hullOnLandImportEndDate",
    isResizable: true,
    fieldName: "hullOnLandImportEndDate",
    labelKey: "bfm.hullOnLandImportEndDate.label",
    name: "bfm.hullOnLandImportEndDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.hullOnLandImportEndDate
          ? new Date(row?.hullOnLandImportEndDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.hullOnLandImportEndDate
            ? formatDateString(row?.hullOnLandImportEndDate)
            : null}
        </span>
      );
    },
  },

  {
    key: "hullOnLandComment",
    isResizable: true,
    fieldName: "hullOnLandComment",
    labelKey: "bfm.hullOnLandComment.label",
    name: "bfm.hullOnLandComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
  },

  {
    key: "hullOnLandRelatedBookingAmount",
    isResizable: true,
    fieldName: "hullOnLandRelatedBookingAmount",
    labelKey: "bfm.hullOnLandRelatedBookingAmount.label",
    name: "bfm.hullOnLandRelatedBookingAmount.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.hullOnLandRelatedBookingAmount === null) return null;
        return row?.hullOnLandRelatedBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.hullOnLandRelatedBookingAmount === null) return null;
      return formatNumber(row.hullOnLandRelatedBookingAmount);
    },
  },

  {
    key: "machineryBreakdownPolicyNumber",
    isResizable: true,
    fieldName: "machineryBreakdownPolicyNumber",
    labelKey: "bfm.fleetForm.machineryBreakdownPolicyNumber.label",
    name: "bfm.fleetForm.machineryBreakdownPolicyNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "text",
      value: "",
    },
  },
  {
    key: "machineryBreakdownIsPolicyIssued",
    isResizable: true,
    fieldName: "machineryBreakdownIsPolicyIssued",
    labelKey: "bfm.fleetForm.machineryBreakdownIsPolicyIssued.label",
    name: "bfm.fleetForm.machineryBreakdownIsPolicyIssued.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    excel: {
      render: ({ t, row }) =>
        row?.machineryBreakdownIsPolicyIssued ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.machineryBreakdownIsPolicyIssued} />;
    },
  },
  {
    key: "machineryBreakdownPolicyDocumentDate",
    isResizable: true,
    fieldName: "machineryBreakdownPolicyDocumentDate",
    labelKey: "bfm.fleetForm.machineryBreakdownPolicyDocumentDate.label",
    name: "bfm.fleetForm.machineryBreakdownPolicyDocumentDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.machineryBreakdownPolicyDocumentDate
          ? new Date(row?.machineryBreakdownPolicyDocumentDate)
          : null,
    },

    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.machineryBreakdownPolicyDocumentDate
            ? formatDateString(row?.machineryBreakdownPolicyDocumentDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "machineryBreakdownInsurerPartnerNumber",
    isResizable: true,
    fieldName: "machineryBreakdownInsurerPartnerNumber",
    labelKey: "bfm.fleetForm.machineryBreakdownInsurerPartnerNumber.label",
    name: "bfm.fleetForm.machineryBreakdownInsurerPartnerNumber.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "taxonomy",
      isSelect: true,
      value: [],
      taxonomyKey: "machineryBreakdownInsurerPartnerNumber",
      renderLabel: ({ taxonomy, t }: any) => {
        return <>{t(taxonomy.code)}</>;
      },
    },
    onRender: (row) => {
      if (!row) return null;
      return (
        <ColumnHoverCard vehicle={row}>
          <LineInsurer
            partnerNumber={row?.machineryBreakdownInsurerPartnerNumber}
          />
        </ColumnHoverCard>
      );
    },
    excel: {
      render: ({ row, t, insurerList }) => {
        const insurer1 = insurerList.find(
          (insurer) =>
            insurer.insurerInternalNumber + "" ===
            row.machineryBreakdownInsurerPartnerNumber,
        );
        const insurerName1 = insurer1 ? insurer1?.insurerName : "";
        return insurerName1;
      },
    },
  },
  {
    key: "machineryBreakdownAnnualGrossPremium",
    isResizable: true,
    fieldName: "machineryBreakdownAnnualGrossPremium",
    labelKey: "bfm.machineryBreakdownProRataAnnualGrossPremium.label",
    name: "bfm.machineryBreakdownProRataAnnualGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.machineryBreakdownAnnualGrossPremium === null)
          return null;
        return row?.machineryBreakdownAnnualGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.machineryBreakdownAnnualGrossPremium === null)
        return null;
      return formatNumber(row.machineryBreakdownAnnualGrossPremium);
    },
  },
  {
    key: "bookingAmountMachineryBreakdown",
    isResizable: true,
    fieldName: "bookingAmountMachineryBreakdown",
    labelKey: "bfm.bookingAmountMachineryBreakdown.label",
    name: "bfm.bookingAmountMachineryBreakdown.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: false,
    excel: {
      type: "number",
      render: ({ row, taxonomy }) =>
        getBookingAmount(row?.machineryBreakdownAnnualGrossPremium, taxonomy, row?.machineryBreakdownPaymentMethod),
    },
    onRender: (row) => (
      <BookingAmountCell
        annualValue={row?.machineryBreakdownAnnualGrossPremium}
        paymentMethodCode={row?.machineryBreakdownPaymentMethod}
      />
    ),
  },
  {
    key: "machineryBreakdownPaymentMethod",
    isResizable: true,
    fieldName: "machineryBreakdownPaymentMethod",
    labelKey: "bfm.machineryBreakdownPaymentMethod.label",
    isSortable: true,
    name: "bfm.machineryBreakdownPaymentMethod.label",
    minWidth: 150,
    iconName: "Sort",
    maxWidth: 150,
    isVisible: false,
    placeholder: "bfm.columnOptions.typeToStartSearching",
    filter: {
      type: "taxonomy",
      taxonomyKey: "PaymentMethod",
      value: [],
    },
    excel: {
      width: 200,
      render: ({ t, taxonomy, row }) =>
        t(taxonomy.PaymentMethod.byId[row?.machineryBreakdownPaymentMethod]?.code),
      isSortable: false,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <CodeCell
          property={"PaymentMethod"}
          propertyCode={row?.machineryBreakdownPaymentMethod}
        />
      );
    },
  },
  {
    key: "machineryBreakdownProRataGrossPremium",
    isResizable: true,
    fieldName: "machineryBreakdownProRataGrossPremium",
    labelKey: "bfm.machineryBreakdownProrataGrossPremium.label",
    name: "bfm.machineryBreakdownProrataGrossPremium.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.machineryBreakdownProRataGrossPremium === null)
          return null;
        return row?.machineryBreakdownProRataGrossPremium;
      },
    },
    onRender: (row) => {
      if (!row || row?.machineryBreakdownProRataGrossPremium === null)
        return null;
      return formatNumber(row.machineryBreakdownProRataGrossPremium);
    },
  },
  {
    key: "machineryBreakdownIsClosingDate",
    isResizable: true,
    fieldName: "machineryBreakdownIsClosingDate",
    labelKey: "bfm.machineryBreakdownIsClosingDate.label",
    name: "bfm.machineryBreakdownIsClosingDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",

    excel: {
      render: ({ t, row }) =>
        row?.machineryBreakdownIsClosingDate ? t("greco.yes") : "",
    },
    onRender: (row) => {
      if (!row) return;

      return <CheckColumn value={row?.machineryBreakdownIsClosingDate} />;
    },
  },
  {
    key: "machineryBreakdownLastStartDate",
    isResizable: true,
    fieldName: "machineryBreakdownLastStartDate",
    labelKey: "bfm.machineryBreakdownLastStartDate.label",
    name: "bfm.machineryBreakdownLastStartDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.machineryBreakdownLastStartDate
          ? new Date(row?.machineryBreakdownLastStartDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.machineryBreakdownLastStartDate
            ? formatDateString(row?.machineryBreakdownLastStartDate)
            : null}
        </span>
      );
    },
  },
  {
    key: "machineryBreakdownImportEndDate",
    isResizable: true,
    fieldName: "machineryBreakdownImportEndDate",
    labelKey: "bfm.machineryBreakdownImportEndDate.label",
    name: "bfm.machineryBreakdownImportEndDate.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "date",
      date1: null,
      date2: null,
      operator: "isBetween",
    },
    excel: {
      type: "date",
      render: ({ row }) =>
        row?.machineryBreakdownImportEndDate
          ? new Date(row?.machineryBreakdownImportEndDate)
          : null,
    },
    onRender: (row) => {
      if (!row) return;
      return (
        <span>
          {row?.machineryBreakdownImportEndDate
            ? formatDateString(row?.machineryBreakdownImportEndDate)
            : null}
        </span>
      );
    },
  },

  {
    key: "machineryBreakdownComment",
    isResizable: true,
    fieldName: "machineryBreakdownComment",
    labelKey: "bfm.machineryBreakdownComment.label",
    name: "bfm.machineryBreakdownComment.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
  },

  {
    key: "machineryBreakdownRelatedBookingAmount",
    isResizable: true,
    fieldName: "machineryBreakdownRelatedBookingAmount",
    labelKey: "bfm.machineryBreakdownRelatedBookingAmount.label",
    name: "bfm.machineryBreakdownRelatedBookingAmount.label",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.machineryBreakdownRelatedBookingAmount === null)
          return null;
        return row?.machineryBreakdownRelatedBookingAmount;
      },
    },
    onRender: (row) => {
      if (!row || row?.machineryBreakdownRelatedBookingAmount === null)
        return null;
      return formatNumber(row.machineryBreakdownRelatedBookingAmount);
    },
  },
  {
    key: "machineryBreakdownListPrice",
    isResizable: true,
    fieldName: "machineryBreakdownListPrice",
    labelKey: "bfm.vehicleForm.machineryBreakdownListPrice.label",
    name: "bfm.vehicleForm.machineryBreakdownListPrice.label",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    isSortable: true,
    iconName: "Sort",
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
      // includeNumber1: true,
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.machineryBreakdownListPrice === null) return null;
        return parseFloat(row.machineryBreakdownListPrice);
      },
    },
    onRender: (row) => {
      if (!row || row?.machineryBreakdownListPrice === null) return null;
      const parsedNumber = parseFloat(row.machineryBreakdownListPrice);
      return formatNumber(parsedNumber);
    },
  },
  {
    key: "machineryBreakdownListPriceTypeCode",
    isResizable: true,
    fieldName: "machineryBreakdownListPriceTypeCode",
    labelKey: "bfm.vehicleForm.machineryBreakdownListPriceTypeCode.label",
    name: "bfm.vehicleForm.machineryBreakdownListPriceTypeCode.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "taxonomy",
      taxonomyKey: "ListPriceType",
      value: [],
    },
    excel: {
      render: ({ t, taxonomy, row }) =>
        t(
          taxonomy.ListPriceType.byId[row?.machineryBreakdownListPriceTypeCode]
            ?.code,
        ),
    },
    onRender: (row) => (
      <CodeCell
        property={"MachineryBreakdownListPriceType"}
        propertyCode={row?.machineryBreakdownListPriceTypeCode}
      />
    ),
  },
  {
    key: "machineryBreakdownSpecialEquipment",
    isResizable: true,
    fieldName: "machineryBreakdownSpecialEquipment",
    labelKey: "bfm.vehicleForm.machineryBreakdownSpecialEquipment.label",
    name: "bfm.vehicleForm.machineryBreakdownSpecialEquipment.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 200,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "number",
      number1: null,
      number2: null,
      operator: "gt",
    },
    excel: {
      type: "number",
      render: ({ row }) => {
        if (!row || row?.machineryBreakdownSpecialEquipment === null)
          return null;
        return parseFloat(row.machineryBreakdownSpecialEquipment);
      },
    },
    onRender: (row) => {
      if (!row || row?.machineryBreakdownSpecialEquipment === null) return null;
      const parsedNumber = parseFloat(row.machineryBreakdownSpecialEquipment);
      return formatNumber(parsedNumber);
    },
  },
  {
    key: "machineryBreakdownIsIncludingTax",
    isResizable: true,
    fieldName: "machineryBreakdownIsIncludingTax",
    labelKey: "bfm.vehicleForm.machineryBreakdownIsIncludingTax.label",
    name: "bfm.vehicleForm.machineryBreakdownIsIncludingTax.label",
    isSortable: true,
    iconName: "Sort",
    minWidth: 150,
    maxWidth: 200,
    isVisible: false,
    filter: {
      type: "boolean",
      value: null,
    },
    excel: {
      render: ({ t, row }) =>
        row?.machineryBreakdownIsIncludingTax ? t("greco.yes") : "",
    },
    onRender: (row) => {
      return <CheckColumn value={row?.machineryBreakdownIsIncludingTax} />;
    },
  },
];
// .sort(function (a, b) {
//   if (a.key < b.key) return -1;

//   return 0;
// });
export const defaultColumnsByKey = defaultColumns?.reduce((byKey, c) => {
  byKey[c.key] = c;
  return byKey;
}, {});
